import type {
  BigNumber,
  BigNumberish,
  BytesLike,
  ContractTransaction,
} from 'ethers';
import { utils } from 'ethers';

import type { MogulERC1155Mintable, MogulLaunchpad } from '../lib/contracts';
import {
  MogulERC1155MintableFactory,
  MogulLaunchpadFactory,
} from '../lib/contracts';
import {
  ERC1155MINTABLE_ADDRESS,
  NFT_ERC_721_CONTRACT_ADDRESS,
} from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';

export const useLaunchpadContract = (
  props = { launchPadContractAddress: '' },
) => {
  const { contract, ...walletInfo } = useContract<MogulLaunchpad>(
    MogulLaunchpadFactory,
    NFT_ERC_721_CONTRACT_ADDRESS,
    props.launchPadContractAddress,
  );

  const totalSupply = async () => {
    return contract?.totalSupply();
  };

  const presaleIsActive = async () => {
    return contract?.presaleIsActive();
  };

  const setApprovalForAll = async (operator: string, approved: boolean) => {
    try {
      if (!contract) {
        return false;
      }

      const trx = await contract.setApprovalForAll(operator, approved);

      await trx.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'launchpad-contract.hooks.ts',
          function: 'setApprovalForAll',
        },
        contexts: {},
      });
    }

    return false;
  };

  const getlaunchpadApprovalStatus = async (
    account: string,
    operator: string,
  ) => {
    if (!account || !operator) {
      return false;
    }

    const approvalStatus = await contract?.isApprovedForAll(account, operator);

    return approvalStatus ?? false;
  };

  const mintPreSale = async (
    proof: BytesLike[],
    amount: BigNumberish,
    ethCost: string,
    isStarPurchase: boolean,
  ) => {
    try {
      if (!contract) {
        return { presale: null, isSuccessful: false, error: null };
      }

      let mintPresale: ContractTransaction;
      if (isStarPurchase) {
        mintPresale = await contract.mintPresale(proof, amount, isStarPurchase);
      } else {
        mintPresale = await contract.mintPresale(
          proof,
          amount,
          isStarPurchase,
          { value: utils.parseEther(ethCost).mul(amount) },
        );
      }

      await mintPresale.wait();

      return { presale: mintPresale, isSuccessful: true, error: null };
    } catch (e) {
      console.error('minting presale failed', e);
      window.Sentry?.captureException(e, {
        tags: { file: 'launchpad-contract.hooks.ts', function: 'mintPreSale' },
        contexts: {},
      });

      /** @ts-expect-error */
      return { presale: null, isSuccessful: false, error: e.error };
    }
  };

  const mint = async (
    amount: BigNumberish,
    ethCost: string,
    isStarPurchase: boolean,
  ) => {
    try {
      if (!contract) {
        return { sale: null, isSuccessful: false, error: null };
      }

      let mintSale: ContractTransaction;
      if (isStarPurchase) {
        mintSale = await contract.mint(amount, isStarPurchase);
      } else {
        mintSale = await contract.mint(amount, isStarPurchase, {
          value: utils.parseEther(ethCost).mul(amount),
        });
      }

      await mintSale.wait();

      return { sale: mintSale, isSuccessful: true, error: null };
    } catch (e) {
      console.error('minting presale failed', e);
      window.Sentry?.captureException(e, {
        tags: { file: 'launchpad-contract.hooks.ts', function: 'mint' },
        contexts: {},
      });

      /** @ts-expect-error */
      return { sale: null, isSuccessful: false, error: e.error };
    }
  };

  return {
    contractInitialized: contract !== undefined,
    walletInfo,
    totalSupply,
    presaleIsActive,
    setApprovalForAll,
    getlaunchpadApprovalStatus,
    mintPreSale,
    mint,
  };
};

export const useERC1155MintableLaunchpadContract = (
  props = {
    launchPadContractAddress: '',
  },
) => {
  const { contract, ...walletInfo } = useContract<MogulERC1155Mintable>(
    MogulERC1155MintableFactory,
    ERC1155MINTABLE_ADDRESS,
    props.launchPadContractAddress,
  );

  const getUserBalances = async (
    from: number,
    to: number,
    userAddress: string,
  ) => {
    const addressesArray: string[] = [];
    const tokenIds: number[] = [];

    for (let x = from; x <= to; x += 1) {
      addressesArray.push(userAddress);
      tokenIds.push(x);
    }

    const balancesUnformatted = await contract?.balanceOfBatch(
      addressesArray,
      tokenIds,
    );

    const balances = balancesUnformatted?.map(v => v.toNumber());

    return balances ?? [];
  };

  const getTokenIdSupply = async (tokenId: number) => {
    const supply = await contract?.totalSupply(tokenId);

    return supply?.toNumber() ?? 0;
  };

  const getTotalSupplies = async (from: number, to: number) => {
    // We will get supply of those and the last minted
    const response = await contract?.getTotalSupplies(from, to);
    const supplies = response ? response[0] : [];
    const lastMintedTokenId = response ? response[1] : 0;

    const formattedSupplies: number[] = supplies.map(v =>
      parseInt(v.toString(), 10),
    );

    const lastMintedTokenIdFormatted: number = parseInt(
      lastMintedTokenId.toString(),
      10,
    );

    return {
      supplies: formattedSupplies,
      lastMintedTokenId: lastMintedTokenIdFormatted,
    };
  };

  const setApprovalForAll = async (operator: string, approved: boolean) => {
    try {
      if (!contract) {
        return false;
      }

      const approvalTransaction = await contract.setApprovalForAll(
        operator,
        approved,
      );

      await approvalTransaction.wait();

      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'launchpad-contract.hooks.ts',
          function: 'setApprovalForAll',
        },
        contexts: {},
      });
    }

    return false;
  };

  const getlaunchpadApprovalStatus = async (
    account: string,
    operator: string,
  ) => {
    if (!account || !operator) {
      return false;
    }

    const launchpadApprovalStatus = await contract?.isApprovedForAll(
      account,
      operator,
    );

    return launchpadApprovalStatus ?? false;
  };

  const mintLaunchpadTokens = async (
    tokenId: number,
    amount: number,
    currency: string,
    pricingStrategy: string,
    merkleProof: BytesLike[],
  ) => {
    // Tiered - each tear can be assumed to be one single tokenID (.to and .from are equal)
    // 2. totalSupply() of tokenId => See how many can still be minted
    // 3. Provide error/validation message if amount exceeds limit per transaction or remaining token supply
    // 4. Verify if whitelisted, if so, generate proof
    // 5. Make use of mintToken method, provide proof is length greater than zero
    // TODO: maybe use getCost or getPrice method to confirm frontend and transaction consumption

    // Simplified strategy
    // 1. Behaves just like a ERC721 token, if they ask 5 to be minted, tokenid 0, 1, 2, 3, 4 are provided
    // 2. Check last minted token to control which to default ui to
    // Tiered Strategy
    // 2. Each tokenId has a total supply, if they ask 3 to be minted and select tokenId4, 3 tokens of tokenId4 are minted

    // TODO: getTokenUriMapping check it in admin-ui probably passing invalid data in the check;
    try {
      if (!contract) {
        return { sale: null, isSuccessful: false, error: null };
      }

      const convertBigNum = (val: BigNumber) => {
        return parseInt(val.toString(), 10);
      };

      const rawResponses = await Promise.all([
        contract.maxQuantityGlobal(),
        contract.maxQuantity(tokenId),
        contract.maxMintsPerTxGlobal(),
        contract.totalSupply(tokenId),
      ]);

      const maxQuantityGlobal = convertBigNum(rawResponses[0]);
      const maxQuantityToken = convertBigNum(rawResponses[1]);
      const txLimit = convertBigNum(rawResponses[2]);
      const currentSupply = convertBigNum(rawResponses[3]);

      const maxQuantity =
        maxQuantityToken > maxQuantityGlobal
          ? maxQuantityToken
          : maxQuantityGlobal;

      let exceedsAmount = false;

      if (pricingStrategy === 'Tiered') {
        exceedsAmount = maxQuantity > 0 && maxQuantity < currentSupply + amount;
      }

      if (txLimit && amount > txLimit) {
        exceedsAmount = true;
      }

      if (exceedsAmount) {
        throw Error('Will exceed mint limit');
      }

      let mintSale: ContractTransaction;
      const nativeAddress = '0x0000000000000000000000000000000000000000';

      // If tiered, only targetId, if not then an array of tokenIds starting with tokenId
      const tokenIds = pricingStrategy === 'Tiered' ? [tokenId] : [];
      const mintAmounts = pricingStrategy === 'Tiered' ? [amount] : [];
      if (pricingStrategy === 'Simplified') {
        for (let x = 0; x < amount; x += 1) {
          tokenIds.push(tokenId + x);
          // We only mint one of each
          mintAmounts.push(1);
        }
      }
      const cost: BigNumber = await contract.getTotalCost(
        currency,
        tokenIds,
        mintAmounts,
      );

      if (pricingStrategy === 'Tiered' || mintAmounts.length === 1) {
        if (currency === nativeAddress) {
          mintSale = await contract.mintToken(
            walletInfo.address ?? '',
            tokenIds[0],
            mintAmounts[0],
            currency,
            [],
            merkleProof as any[],
            { value: cost },
          );
        } else {
          mintSale = await contract.mintToken(
            walletInfo.address ?? '',
            tokenId,
            amount,
            currency,
            [],
            merkleProof as any[],
          );
        }
      } else if (currency === nativeAddress) {
        mintSale = await contract.mintBatch(
          walletInfo.address ?? '',
          tokenIds,
          mintAmounts,
          currency,
          [],
          merkleProof as any[],
          { value: cost },
        );
      } else {
        mintSale = await contract.mintBatch(
          walletInfo.address ?? '',
          tokenIds,
          mintAmounts,
          currency,
          [],
          merkleProof as any[],
        );
      }

      await mintSale.wait();

      return { sale: mintSale, isSuccessful: true, error: null };
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'launchpad-contract.hooks.ts', function: 'mint' },
        contexts: {},
      });

      /** @ts-expect-error */
      return { sale: null, isSuccessful: false, error: e.error };
    }
  };

  return {
    ERC1155ContractInitialized:
      contract !== undefined &&
      contract.address === props.launchPadContractAddress,
    walletInfo,
    getUserBalances,
    getTokenIdSupply,
    getTotalSupplies,
    mintLaunchpadTokens,
    setApprovalForAll,
    getlaunchpadApprovalStatus,
  };
};
